import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  CalendarDays, 
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Briefcase,
  X
} from "lucide-react";

const CalendarPage = () => {
  const { token, hasRole } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [eventPopupOpen, setEventPopupOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const [formData, setFormData] = useState({
    naslov: "",
    opis: "",
    datum: "",
    cas: "",
    lokacija: "",
    tip: "dogodek"
  });

  useEffect(() => {
    fetchEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/events`, { headers });
      setEvents(response.data);
    } catch (error) {
      toast.error("Napaka pri nalaganju dogodkov");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/events`, formData, { headers });
      toast.success("Dogodek dodan");
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  const resetForm = () => {
    setFormData({
      naslov: "",
      opis: "",
      datum: "",
      cas: "",
      lokacija: "",
      tip: "dogodek"
    });
  };

  const getEventTypeLabel = (tip) => {
    const labels = {
      dogodek: "Dogodek",
      sestanek: "Sestanek",
      lov: "Lov",
      delovna_akcija: "Delovna akcija"
    };
    return labels[tip] || tip;
  };

  const getEventTypeColor = (tip) => {
    const colors = {
      dogodek: "bg-blue-500",
      sestanek: "bg-purple-500",
      sestanek_uo: "bg-red-500",
      lov: "bg-green-500",
      delovna_akcija: "bg-orange-500",
      preizkus: "bg-amber-500"
    };
    return colors[tip] || "bg-gray-500";
  };

  const getEventTypeBadgeColor = (tip) => {
    const colors = {
      dogodek: "bg-blue-100 text-blue-800",
      sestanek: "bg-purple-100 text-purple-800",
      sestanek_uo: "bg-red-100 text-red-800",
      lov: "bg-green-100 text-green-800",
      delovna_akcija: "bg-orange-100 text-orange-800",
      preizkus: "bg-amber-100 text-amber-800"
    };
    return colors[tip] || "bg-gray-100 text-gray-800";
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay(); // 0 = Sunday
    
    // Adjust for Monday start (European)
    const adjustedStartDay = startingDay === 0 ? 6 : startingDay - 1;
    
    return { daysInMonth, adjustedStartDay };
  };

  const getEventsForDate = (date) => {
    const dateStr = formatDateISO(date);
    return events.filter(e => e.datum === dateStr);
  };

  const formatDateISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatMonthYear = (date) => {
    const months = [
      "Januar", "Februar", "Marec", "April", "Maj", "Junij",
      "Julij", "Avgust", "September", "Oktober", "November", "December"
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatDateFull = (dateStr) => {
    const date = new Date(dateStr);
    const days = ["nedelja", "ponedeljek", "torek", "sreda", "četrtek", "petek", "sobota"];
    const months = [
      "januar", "februar", "marec", "april", "maj", "junij",
      "julij", "avgust", "september", "oktober", "november", "december"
    ];
    return `${days[date.getDay()]}, ${date.getDate()}. ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const handleDayClick = (day) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const eventsOnDay = getEventsForDate(clickedDate);
    
    setSelectedDate(clickedDate);
    setSelectedEvents(eventsOnDay);
    setEventPopupOpen(true);
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      currentMonth.getFullYear() === today.getFullYear() &&
      currentMonth.getMonth() === today.getMonth() &&
      day === today.getDate()
    );
  };

  const canManage = hasRole(["administrator", "predsednik", "gospodar"]);

  const { daysInMonth, adjustedStartDay } = getDaysInMonth(currentMonth);
  const weekDays = ["Pon", "Tor", "Sre", "Čet", "Pet", "Sob", "Ned"];

  // Create calendar grid
  const calendarDays = [];
  // Empty cells before first day
  for (let i = 0; i < adjustedStartDay; i++) {
    calendarDays.push(null);
  }
  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-3">
            <CalendarDays className="h-7 w-7 sm:h-8 sm:w-8" />
            Koledar
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {events.length} dogodkov
          </p>
        </div>
        
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-calendar-event-btn" className="w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Dodaj dogodek
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md mx-4">
              <DialogHeader>
                <DialogTitle>Nov dogodek</DialogTitle>
                <DialogDescription>
                  Dodajte dogodek ali delovno akcijo v koledar
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="naslov">Naslov</Label>
                  <Input
                    id="naslov"
                    value={formData.naslov}
                    onChange={(e) => setFormData({ ...formData, naslov: e.target.value })}
                    placeholder="Ime dogodka"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tip">Tip</Label>
                  <Select 
                    onValueChange={(value) => setFormData({ ...formData, tip: value })} 
                    value={formData.tip}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Izberite tip" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dogodek">Dogodek</SelectItem>
                      <SelectItem value="sestanek">Sestanek</SelectItem>
                      <SelectItem value="lov">Lov</SelectItem>
                      <SelectItem value="delovna_akcija">Delovna akcija</SelectItem>
                      <SelectItem value="preizkus">Preizkus orožja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="datum">Datum</Label>
                    <Input
                      id="datum"
                      type="date"
                      value={formData.datum}
                      onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cas">Ura</Label>
                    <Input
                      id="cas"
                      type="time"
                      value={formData.cas}
                      onChange={(e) => setFormData({ ...formData, cas: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lokacija">Lokacija</Label>
                  <Input
                    id="lokacija"
                    value={formData.lokacija}
                    onChange={(e) => setFormData({ ...formData, lokacija: e.target.value })}
                    placeholder="Kraj dogodka"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="opis">Opis</Label>
                  <Textarea
                    id="opis"
                    value={formData.opis}
                    onChange={(e) => setFormData({ ...formData, opis: e.target.value })}
                    placeholder="Opis dogodka"
                    rows={2}
                  />
                </div>

                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Prekliči
                  </Button>
                  <Button type="submit">Dodaj</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
          <span>Dogodek</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div>
          <span>Sestanek</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
          <span>Lov</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
          <span>Delovna akcija</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
          <span>Preizkus</span>
        </div>
      </div>

      {/* Calendar */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg sm:text-xl">{formatMonthYear(currentMonth)}</CardTitle>
              <Button variant="outline" size="sm" onClick={goToToday} className="text-xs">
                Danes
              </Button>
            </div>
            <Button variant="ghost" size="icon" onClick={goToNextMonth}>
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Week days header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} className="aspect-square"></div>;
              }
              
              const dateForDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
              const eventsOnDay = getEventsForDate(dateForDay);
              const hasEvents = eventsOnDay.length > 0;
              const today = isToday(day);
              
              // Get background color based on event type
              const getEventDayBg = () => {
                if (!hasEvents) return '';
                const event = eventsOnDay[0]; // Primary event determines color
                const colorMap = {
                  'dogodek': 'bg-blue-100 hover:bg-blue-200',
                  'sestanek': 'bg-purple-100 hover:bg-purple-200',
                  'sestanek_uo': 'bg-red-100 hover:bg-red-200',
                  'lov': 'bg-green-100 hover:bg-green-200',
                  'delovna_akcija': 'bg-orange-100 hover:bg-orange-200',
                  'preizkus': 'bg-amber-100 hover:bg-amber-200'
                };
                return colorMap[event.tip] || 'bg-gray-100 hover:bg-gray-200';
              };
              
              return (
                <div
                  key={day}
                  className={`
                    aspect-square p-1 rounded-lg cursor-pointer transition-all relative
                    ${today ? 'ring-2 ring-primary ring-offset-1' : ''}
                    ${hasEvents ? getEventDayBg() : 'hover:bg-muted'}
                  `}
                  onClick={() => handleDayClick(day)}
                  data-testid={`calendar-day-${day}`}
                >
                  <div className={`text-center text-sm ${today ? 'text-primary font-bold' : ''} ${hasEvents ? 'font-semibold' : ''}`}>
                    {day}
                  </div>
                  
                  {/* Event count badge */}
                  {hasEvents && (
                    <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2">
                      {eventsOnDay.length > 1 ? (
                        <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 min-w-4">
                          {eventsOnDay.length}
                        </Badge>
                      ) : (
                        <div className={`w-2.5 h-2.5 rounded-full ${getEventTypeColor(eventsOnDay[0].tip)}`} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Event Popup Dialog */}
      <Dialog open={eventPopupOpen} onOpenChange={setEventPopupOpen}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {selectedDate && formatDateFull(selectedDate.toISOString())}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {selectedEvents.length === 0 ? (
              <div className="text-center py-8">
                <CalendarDays className="h-10 w-10 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Ni dogodkov na ta dan</p>
                {canManage && (
                  <Button 
                    variant="outline" 
                    className="mt-4" 
                    onClick={() => {
                      setEventPopupOpen(false);
                      setFormData({
                        ...formData,
                        datum: formatDateISO(selectedDate)
                      });
                      setDialogOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Dodaj dogodek
                  </Button>
                )}
              </div>
            ) : (
              selectedEvents.map((event) => (
                <div 
                  key={event.id}
                  className="p-4 rounded-lg border bg-card"
                  data-testid={`event-popup-${event.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-1 h-full min-h-[60px] rounded-full ${getEventTypeColor(event.tip)}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${getEventTypeBadgeColor(event.tip)} text-xs`}>
                          {event.tip === 'delovna_akcija' && <Briefcase className="h-3 w-3 mr-1" />}
                          {getEventTypeLabel(event.tip)}
                        </Badge>
                        {event.cas && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {event.cas}
                          </span>
                        )}
                      </div>
                      
                      <h4 className="font-semibold text-base">{event.naslov}</h4>
                      
                      {event.opis && (
                        <p className="text-sm text-muted-foreground mt-2">
                          {event.opis}
                        </p>
                      )}
                      
                      {event.lokacija && (
                        <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {event.lokacija}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventPopupOpen(false)}>
              Zapri
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
